import { EventEmitter, Injectable } from '@angular/core';
import { waitSeconds } from '@reflact/tsutil';
import { isKeyValEntryUnkownData, KeyValEntry } from '@shared/KeyValStore';
import { keyValGetSchema, keyValSetSchema } from '@shared/ragai.schemas';
import dayjs from 'dayjs';
import { z } from 'zod';
import { EventManagerService } from './event-manager/event-manager.service';
import { SocketService } from './socket.service';

/**
 * Speichert nutzer- und bot-spezifische Schlüssel-Wert-Paare zur Laufzeit (`RAGAI.KEYVAL`).
 * Optional lässt sich jedem Eintrag ein Typ zur Kategorisierung zuweisen; Schlüssel mit `!`-Präfix werden dauerhaft gespeichert.
 * @remarks
 * Ohne `!`-Präfix wird ein Eintrag beim Reset der Bot-Sitzung gelöscht (z. B. nach `RAGAI.resetBot()`); mit
 * `!`-Präfix (z. B. `!sprache`) übersteht er Session-Resets und ist beim nächsten Besuch desselben Nutzers wieder da.
 * Der Speicher ist **pro Bot** isoliert - für projektweite, botübergreifende Daten desselben Nutzers gibt es
 * stattdessen `RAGAI.PROJECTKEYVAL` ({@link ProjectKeyValService}) mit identischer API.
 * Updates/Löschungen werden serverseitig per Socket synchronisiert (`onKeyValUpdate`/`onKeyValDelete`), damit
 * mehrere gleichzeitig offene Tabs/Frames desselben Nutzers konsistent bleiben.
 */
@Injectable({
  providedIn: 'root'
})
export class KeyValService {
  /** Wird ausgelöst, wenn ein Schlüssel-Wert-Paar gesetzt oder aktualisiert wird. */
  public onKeyValUpdate: EventEmitter<KeyValEntry> = new EventEmitter<KeyValEntry>();
  /** Wird ausgelöst, wenn ein Schlüssel-Wert-Paar gelöscht wird. */
  public onKeyValDelete: EventEmitter<KeyValEntry> = new EventEmitter<KeyValEntry>();

  public keyValMap = new Map<string, KeyValEntry>();

  public initDone = false;

  constructor(
    private socketService: SocketService,
    private eventManager: EventManagerService
  ) {
    void this.broadCastInitIsDone();
    void this.init();
  }

  private getMapKey(key: string, type: string) {
    return `${type}-${key}`;
  }
  private getMapKeyByEntry(entry: KeyValEntry) {
    return this.getMapKey(entry.key, entry.type);
  }

  /** @internal Baut den internen Socket-Verbindungsaufbau und füllt `keyValMap` beim Start. */
  public async init() {

    const io = await this.socketService.getIo();
    io.on('keyValUpdate', (entry: KeyValEntry) => {
      this.onKeyValUpdate.emit(entry);
      if (isKeyValEntryUnkownData(entry)) {
        this.keyValMap.set(this.getMapKeyByEntry(entry), entry);
      }
    });
    io.on('keyValDelete', (entry: KeyValEntry) => {
      this.onKeyValDelete.emit(entry);
      if (isKeyValEntryUnkownData(entry)) {
        this.keyValMap.delete(this.getMapKeyByEntry(entry));
      }
    });
    const items = await this.getInitialValues();
    for (const item of items) {
      if (isKeyValEntryUnkownData(item)) {
        this.keyValMap.set(this.getMapKeyByEntry(item), item);
      }
    }
    this.initDone = true;
  }

  /**
   * @internal Meldet per Event, sobald die initiale Synchronisierung abgeschlossen ist.
   * @returns Nichts.
   */
  public async broadCastInitIsDone() {
    await this.waitForInit();
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.waitForInit", 'KeyVal Init done - keyval.service');
  }
  /**
   * @internal Wartet (busy-polling), bis die initiale Synchronisierung abgeschlossen ist.
   * @returns Immer `null`, sobald die Synchronisierung abgeschlossen ist.
   */
  public async waitForInit() {
    if (this.initDone) {
      return;
    }
    await waitSeconds(0.1);
    await this.waitForInit();
    return null;
  }

  /**
   * @internal Lädt die initialen Einträge vom Server beim Start.
   * @returns Die initialen Einträge.
   */
  public async getInitialValues() {
    const io = await this.socketService.getIo();
    return new Promise<KeyValEntry[]>((resolve, _reject) => {
      io.emit('getInitialKeyVals', (entries: KeyValEntry[]) => {
        resolve(entries);
      });
    });
  }



  /**
   * Löscht einen Eintrag anhand seines Schlüssels und optional seines Typs.
   * @param key Der zu löschende Schlüssel.
   * @param type Optionaler Typ zur Kategorisierung. Standard: `"custom"`.
   * @returns Der gelöschte Eintrag.
   * @example
   * await RAGAI.KEYVAL.deleteKeyValEntry('email', 'userInfo');
   */
  public async deleteKeyValEntry(key: string | z.input<typeof keyValGetSchema>, type: string = "custom"): Promise<KeyValEntry> {
    const parsed = keyValGetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      type = parsed.data.type;
      key = parsed.data.key;
    }
    const k = key as string;
    await this.waitForInit();
    const io = await this.socketService.getIo();
    return new Promise((resolve) => {
      io.emit('removeKeyVal', { key: k, type }, (data: KeyValEntry) => {
        this.keyValMap.delete(this.getMapKey(k, type));
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.deleteKeyValEntry", data);
        resolve(data);
      });
    });
  }

  /**
   * @internal Low-Level-Implementierung von {@link set}; nutze stattdessen `set()`.
   * @param key Eindeutiger Schlüssel oder Objekt-Form (siehe `keyValSetSchema`).
   * @param value Der zu speichernde Wert.
   * @param type Optionaler Typ zur Kategorisierung.
   * @returns Der gespeicherte Eintrag.
   */
  public async storeKeyValEntry(key: string | z.input<typeof keyValSetSchema>, value?: unknown, type: string = "custom"): Promise<KeyValEntry> {
    const parsed = keyValSetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      value = parsed.data.value ?? value;
      type = parsed.data.type;
      key = parsed.data.key;
    }
    const k = key as string;
    await this.waitForInit();
    const io = await this.socketService.getIo();
    return new Promise((resolve) => {
      io.emit('storeKeyValUpdate', { key: k, value: value, type }, (data: KeyValEntry) => {
        this.keyValMap.set(this.getMapKey(k, type), data);
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.storeKeyValEntry", data);
        resolve(data);
      });
    });
  }

  /**
   * @internal Low-Level-Implementierung von {@link get}; nutze stattdessen `get()`.
   * @param key Eindeutiger Schlüssel oder Objekt-Form (siehe `keyValGetSchema`).
   * @param type Optionaler Typ zur Kategorisierung.
   * @returns Der gespeicherte Wert, oder `undefined`.
   */
  public async getKeyValEntry(key: string | z.input<typeof keyValGetSchema>, type: string = "custom"): Promise<unknown> {
    const parsed = keyValGetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      type = parsed.data.type;
      key = parsed.data.key;
    }
    const k = key as string;
    await this.waitForInit();
    const found = this.keyValMap.get(this.getMapKey(k, type));
    if (found) {
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.getKeyValEntry", { key: k, value: found.value });
      return found.value;
    } else {
      return undefined;
    }
  }

  /**
   * @internal Low-Level-Implementierung von {@link getByType}; nutze stattdessen `getByType()`.
   * @param type Der gesuchte Typ.
   * @returns Liste der passenden Einträge.
   */
  public async getKeyValByType(type: string) {
    await this.waitForInit();
    const results: KeyValEntry[] = [];
    this.keyValMap.forEach((val, _key) => {
      if (val.type === type) {
        results.push(val);
      }
    });
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.KEYVAL.getKeyValByType", results);
    return results;
  }

  /**
   * Gibt den Wert des angegebenen Schlüssels zurück, oder `null` wenn er nicht existiert.
   * @param key Eindeutiger Schlüssel.
   * @param type Optionaler Typ zur Kategorisierung. Standard: `"custom"`.
   * @returns Der gespeicherte Wert oder `null`.
   * @example
   * const vorname = await RAGAI.KEYVAL.get('vorname');
   * const email = await RAGAI.KEYVAL.get('email', 'userInfo');
   */
  public async get(key: string | z.input<typeof keyValGetSchema>, type: string = "custom"): Promise<unknown> {
    const parsed = keyValGetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      type = parsed.data.type;
      key = parsed.data.key;
    }
    const val = (await this.getByType(type))
      .find(obj => obj.key === (key as string))
      ?.value ?? null;
    return val;
  }

  /**
   * Speichert einen Wert unter dem angegebenen Schlüssel. Optional lässt sich ein Typ zur Kategorisierung angeben.
   * @param key Eindeutiger Schlüssel. Mit `!`-Präfix für permanente Speicherung.
   * @param value Der zu speichernde Wert (String, Zahl, Objekt, Array etc.).
   * @param type Optionaler Typ zur Kategorisierung. Standard: `"custom"`.
   * @returns Der gespeicherte Eintrag.
   * @example
   * await RAGAI.KEYVAL.set('vorname', 'Anna');
   * await RAGAI.KEYVAL.set('email', 'anna@example.com', 'userInfo');
   * await RAGAI.KEYVAL.set('!sprache', 'de');
   */
  public async set(key: string | z.input<typeof keyValSetSchema>, value?: unknown, type: string = "custom"): Promise<KeyValEntry> {
    const parsed = keyValSetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      value = parsed.data.value ?? value;
      type = parsed.data.type;
      key = parsed.data.key;
    }
    return await this.storeKeyValEntry(key, value, type);
  }
  /**
   * Funktioniert wie `set()`, hängt dem Schlüssel aber automatisch einen Zeitstempel an, damit mehrere
   * Einträge mit demselben Basis-Schlüssel nebeneinander bestehen können (z. B. für Verlaufsdaten).
   * @param key Basis-Schlüssel, wird um einen Zeitstempel ergänzt.
   * @param value Der zu speichernde Wert.
   * @param type Optionaler Typ zur Kategorisierung. Standard: `"custom"`.
   * @returns Der gespeicherte Eintrag.
   * @example
   * await RAGAI.KEYVAL.unique('antwort', 'Ja, das stimmt', 'quiz');
   * await RAGAI.KEYVAL.unique('antwort', 'Nein, falsch', 'quiz');
   */
  public async unique(key: string | z.input<typeof keyValSetSchema>, value?: unknown, type: string = "custom"): Promise<KeyValEntry> {
    const parsed = keyValSetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      value = parsed.data.value ?? value;
      type = parsed.data.type;
      key = parsed.data.key;
    }
    return await this.storeKeyValEntry((key as string) + '_' + dayjs().toISOString(), value, type);
  }
  /**
   * Gibt alle Einträge eines bestimmten Typs als Array von `KeyValEntry`-Objekten zurück.
   * @param type Der gesuchte Typ.
   * @returns Liste der passenden Einträge.
   * @example
   * const userInfos = await RAGAI.KEYVAL.getByType('userInfo');
   */
  public async getByType(type: string) { return this.getKeyValByType(type); }


}
